import { buildPrEnvHeaders, usePrEnvStore } from '@shared/stores/use-pr-env-store';
import { apiClient } from '@shared/utils/api/client';
import { env } from '@shared/utils/env';
import { setRequestHeaderProvider } from '@shared/utils/network-interceptor';

import type { RequestConfig } from '@shared/utils/api/client';

/**
 * Wires the PR-env store into everything that leaves the browser (INF-856).
 *
 * Two paths, because the app has two:
 *
 * - `apiClient.addRequestInterceptor` — the documented one, alongside the existing `x-trace-id` and
 *   `X-Support-Executed-By` interceptors. Covers the ~96 module services.
 * - `setRequestHeaderProvider` — the global `fetch` patch, which covers the file-download flows that
 *   call `fetch` directly. Those are exactly the flows worth testing against an ephemeral pod, and a
 *   miss there is invisible: the download just quietly comes from stable.
 *
 * Both are additive and never overwrite an existing header, so a request going through both gets one
 * set of headers rather than a conflict.
 */

/**
 * @param getHeaders read at call time, not captured — an entry added mid-session applies to the very
 * next request with no re-registration and no React involvement
 */
export const createPrEnvRequestInterceptor =
  (getHeaders: () => Record<string, string>) =>
  (config: RequestConfig): RequestConfig => {
    const extra = getHeaders();
    if (Object.keys(extra).length === 0) return config;

    const headers = { ...((config.headers as Record<string, string>) || {}) };
    Object.entries(extra).forEach(([name, value]) => {
      if (!(name in headers)) headers[name] = value;
    });
    return { ...config, headers } as RequestConfig;
  };

/**
 * Called once from `main.tsx`, at module scope before React mounts.
 *
 * Not from a provider: a session request fires during `AuthProvider` initialisation, which a
 * provider-mounted interceptor would miss, and StrictMode's double-invoked effects would register it
 * twice.
 */
export const installPrEnvHeaders = (): void => {
  if (!env.IS_PR_ENV_ROUTING_AVAILABLE) return;

  const getHeaders = (): Record<string, string> => {
    // Re-checked per request rather than only here: a stale ap_pr_env_store_v1 in localStorage — from
    // a mode switch, or a hostname that once served TST — must never produce an outbound header.
    if (!env.IS_PR_ENV_ROUTING_AVAILABLE) return {};
    return buildPrEnvHeaders(usePrEnvStore.getState().entries);
  };

  apiClient.addRequestInterceptor(createPrEnvRequestInterceptor(getHeaders));
  setRequestHeaderProvider(getHeaders);
};

export default installPrEnvHeaders;
